import type { ReactElement } from 'react';
import { useGamesSite } from '../context';
import type { GameCard } from '../types';
import { GameRow, GameTile } from './GameRow';

/**
 * A titled shelf of games that scrolls sideways. `rows` stacks games three to
 * a column (the store's list shelf); `tiles` stands them up one by one.
 * Nothing renders for an empty shelf.
 */
export function GameShelf({
  id,
  title,
  games,
  seeAllHref,
  seeAllLabel,
  layout = 'rows',
}: {
  /** Unique on the page — names the shelf for assistive technology. */
  id: string;
  title: string;
  games: GameCard[];
  seeAllHref?: string;
  /** What "See all" says to a screen reader, e.g. "See all word games". */
  seeAllLabel?: string;
  layout?: 'rows' | 'tiles';
}): ReactElement | null {
  const { Link } = useGamesSite();
  if (games.length === 0) return null;
  const headingId = `fg-shelf-${id}`;
  return (
    <section className="fg-shelf" aria-labelledby={headingId}>
      <div className="fg-shelf__head">
        <h2 id={headingId} className="fg-h2">
          {title}
        </h2>
        {seeAllHref && (
          <Link href={seeAllHref} className="fg-see-all" aria-label={seeAllLabel}>
            See all
          </Link>
        )}
      </div>
      <ul className={`fg-shelf__list fg-shelf__list--${layout}`}>
        {games.map((game) => (
          <li key={game.slug}>
            {layout === 'rows' ? <GameRow game={game} /> : <GameTile game={game} />}
          </li>
        ))}
      </ul>
    </section>
  );
}

/** A ranked list: the number is the game's place. */
export function TopChart({
  id,
  title,
  games,
  limit = 12,
}: {
  id: string;
  title: string;
  games: GameCard[];
  limit?: number;
}): ReactElement | null {
  if (games.length === 0) return null;
  const headingId = `fg-chart-${id}`;
  return (
    <section className="fg-shelf" aria-labelledby={headingId}>
      <div className="fg-shelf__head">
        <h2 id={headingId} className="fg-h2">
          {title}
        </h2>
      </div>
      <ol className="fg-chart">
        {games.slice(0, limit).map((game, i) => (
          <li key={game.slug}>
            <GameRow game={game} rank={i + 1} />
          </li>
        ))}
      </ol>
    </section>
  );
}
